import type {
  BatchDeclarationGate,
  ExternalToolBatch,
} from './external-tool-batch.types';
import {
  getOrCreateExternalToolBatch,
  isBatchWorkCurrent,
  isToolCallIdKnownToBatch,
  resolveExternalToolBatchAliasIds,
} from './external-tool-batch-gates';
import {
  abortBatchDeclarationGate,
  hasAbortedBatchDeclaration,
  isBatchDeclarationAborted,
  isBatchDeclarationTimedOut,
} from './external-tool-batch-waiters';

export function resolveExternalToolBatchIdentityIds({
  batchId,
  resumeGroupId,
}: {
  batchId: string;
  resumeGroupId?: string;
}): string[] {
  return [...new Set([batchId, ...(resumeGroupId ? [resumeGroupId] : [])])];
}

function batchTouchesToolCallIds({
  batch,
  toolCallIds,
}: {
  batch: ExternalToolBatch;
  toolCallIds: readonly string[];
}): boolean {
  return toolCallIds.some(
    (toolCallId) =>
      batch.expectedToolCallIds.has(toolCallId) ||
      batch.pendingExecuteToolCallIds.has(toolCallId) ||
      batch.requests.has(toolCallId) ||
      batch.results.has(toolCallId),
  );
}

function isHealthyUndeclaredChatSibling({
  batch,
  chatId,
  toolCallIds,
  gates,
}: {
  batch: ExternalToolBatch;
  chatId: string;
  toolCallIds: readonly string[];
  gates: Map<string, BatchDeclarationGate>;
}): boolean {
  if (batch.flushBatchId !== chatId || batch.declared) {
    return false;
  }

  if (
    isBatchDeclarationTimedOut({ gates, batchId: chatId }) ||
    isBatchDeclarationAborted({ gates, batchId: chatId })
  ) {
    return false;
  }

  return batchTouchesToolCallIds({ batch, toolCallIds });
}

export function findKnownChatBatch({
  batches,
  workTokenByChatId,
  chatId,
  toolCallId,
}: {
  batches: Map<string, ExternalToolBatch>;
  workTokenByChatId: Map<string, number>;
  chatId: string;
  toolCallId: string;
}): ExternalToolBatch | undefined {
  const known = [...new Set(batches.values())].find(
    (candidate) =>
      candidate.chatId === chatId &&
      isToolCallIdKnownToBatch({ batch: candidate, toolCallId }),
  );

  if (!known || !isBatchWorkCurrent({ batch: known, workTokenByChatId })) {
    return undefined;
  }

  return known;
}

export function canAttachExecuteToBatch({
  batch,
  batchId,
  toolCallId,
}: {
  batch: ExternalToolBatch;
  batchId: string;
  toolCallId: string;
}): boolean {
  if (batchTouchesToolCallIds({ batch, toolCallIds: [toolCallId] })) {
    return true;
  }

  return !batch.declared && batch.flushBatchId === batchId;
}

export function resolveWritableExecuteBatch({
  batches,
  workTokenByChatId,
  batchId,
  chatId,
  toolCallId,
}: {
  batches: Map<string, ExternalToolBatch>;
  workTokenByChatId: Map<string, number>;
  batchId: string;
  chatId: string;
  toolCallId: string;
}): ExternalToolBatch | undefined {
  const existing = batches.get(batchId);

  if (
    existing &&
    !canAttachExecuteToBatch({ batch: existing, batchId, toolCallId })
  ) {
    return findKnownChatBatch({
      batches,
      workTokenByChatId,
      chatId,
      toolCallId,
    });
  }

  const batch =
    existing ??
    getOrCreateExternalToolBatch({
      batches,
      workTokenByChatId,
      batchId,
      chatId,
    });

  if (batch.chatId !== chatId) {
    batch.chatId = chatId;
  }

  if (!isBatchWorkCurrent({ batch, workTokenByChatId })) {
    if (!existing) {
      batches.delete(batchId);
    }

    return undefined;
  }

  return batch;
}

export function aliasChatIdToWaitingBatch({
  batches,
  chatId,
  skipBatch,
}: {
  batches: Map<string, ExternalToolBatch>;
  chatId: string;
  skipBatch?: ExternalToolBatch;
}): void {
  if (batches.get(chatId)) {
    return;
  }

  const waiting = [...new Set(batches.values())].find(
    (candidate) => candidate.chatId === chatId && candidate !== skipBatch,
  );

  if (waiting) {
    waiting.adoptedOntoChatId = true;
    batches.set(chatId, waiting);
  }
}

export function releaseChatIdOccupancy({
  batches,
  batch,
}: {
  batches: Map<string, ExternalToolBatch>;
  batch: ExternalToolBatch;
}): void {
  const { chatId } = batch;

  if (batches.get(chatId) !== batch) {
    return;
  }

  if (batch.flushBatchId === chatId) {
    batch.adoptedOntoChatId = true;

    return;
  }

  batches.delete(chatId);
  aliasChatIdToWaitingBatch({ batches, chatId, skipBatch: batch });
}

function absorbExternalToolBatch({
  batches,
  source,
  target,
}: {
  batches: Map<string, ExternalToolBatch>;
  source: ExternalToolBatch;
  target: ExternalToolBatch;
}): void {
  if (source === target) {
    return;
  }

  for (const toolCallId of source.pendingExecuteToolCallIds) {
    target.pendingExecuteToolCallIds.add(toolCallId);
  }

  for (const [toolCallId, request] of source.requests) {
    if (!target.requests.has(toolCallId)) {
      target.requests.set(toolCallId, request);
    }
  }

  for (const [toolCallId, result] of source.results) {
    if (!target.results.has(toolCallId)) {
      target.results.set(toolCallId, result);
    }
  }

  for (const [toolCallId, toolName] of source.toolNameByToolCallId) {
    if (!target.toolNameByToolCallId.has(toolCallId)) {
      target.toolNameByToolCallId.set(toolCallId, toolName);
    }
  }

  for (const toolCallId of source.expectedToolCallIds) {
    target.expectedToolCallIds.add(toolCallId);
  }

  for (const toolCallId of source.lostExecuteToolCallIds) {
    target.lostExecuteToolCallIds.add(toolCallId);
  }

  for (const [aliasId, candidate] of batches) {
    if (candidate === source) {
      batches.set(aliasId, target);
    }
  }
}

function tombstoneIncomingWave({
  batchDeclarationGates,
  batchId,
  resumeGroupId,
}: {
  batchDeclarationGates: Map<string, BatchDeclarationGate>;
  batchId: string;
  resumeGroupId?: string;
}): void {
  abortBatchDeclarationGate({
    gates: batchDeclarationGates,
    batchId,
    tombstone: true,
  });

  if (resumeGroupId && resumeGroupId !== batchId) {
    abortBatchDeclarationGate({
      gates: batchDeclarationGates,
      batchId: resumeGroupId,
      tombstone: true,
    });
  }
}

function hasInFlightFlush({
  batches,
  inFlightFlushes,
  occupant,
}: {
  batches: Map<string, ExternalToolBatch>;
  inFlightFlushes: Map<string, Promise<void>>;
  occupant: ExternalToolBatch;
}): boolean {
  for (const [aliasId, candidate] of batches) {
    if (candidate === occupant && inFlightFlushes.has(aliasId)) {
      return true;
    }
  }

  return false;
}

function resolveIdentityExistingBatch({
  batches,
  identityIds,
  batchId,
  chatId,
}: {
  batches: Map<string, ExternalToolBatch>;
  identityIds: readonly string[];
  batchId: string;
  chatId: string;
}): ExternalToolBatch | undefined {
  return identityIds
    .map((identityId) => {
      const candidate = batches.get(identityId);

      if (!candidate) {
        return undefined;
      }

      if (
        identityId === chatId &&
        candidate.flushBatchId !== chatId &&
        candidate.flushBatchId !== batchId
      ) {
        return undefined;
      }

      return candidate;
    })
    .find((candidate) => candidate !== undefined);
}

export function prepareExternalToolBatchOpen({
  batches,
  batchDeclarationGates,
  inFlightFlushes,
  batchId,
  chatId,
  resumeGroupId,
  toolCallIds,
}: {
  batches: Map<string, ExternalToolBatch>;
  batchDeclarationGates: Map<string, BatchDeclarationGate>;
  inFlightFlushes: Map<string, Promise<void>>;
  batchId: string;
  chatId: string;
  resumeGroupId?: string;
  toolCallIds: readonly string[];
}):
  | { status: 'ignored' }
  | {
      status: 'open';
      aliasIds: string[];
      existingBatch: ExternalToolBatch | undefined;
      deferLostExecute?: boolean;
    } {
  const identityIds = resolveExternalToolBatchIdentityIds({
    batchId,
    resumeGroupId,
  });
  const aliasIds = resolveExternalToolBatchAliasIds({
    batchId,
    chatId,
    resumeGroupId,
  });

  if (
    hasAbortedBatchDeclaration({
      gates: batchDeclarationGates,
      batchIds: identityIds,
    }) ||
    identityIds.some((identityId) =>
      isBatchDeclarationTimedOut({
        gates: batchDeclarationGates,
        batchId: identityId,
      }),
    )
  ) {
    return { status: 'ignored' };
  }

  const existingBatch = resolveIdentityExistingBatch({
    batches,
    identityIds,
    batchId,
    chatId,
  });
  const chatOccupant = batches.get(chatId);

  if (!chatOccupant) {
    return { status: 'open', aliasIds, existingBatch };
  }

  if (
    isHealthyUndeclaredChatSibling({
      batch: chatOccupant,
      chatId,
      toolCallIds,
      gates: batchDeclarationGates,
    })
  ) {
    if (existingBatch && chatOccupant !== existingBatch) {
      absorbExternalToolBatch({
        batches,
        source: chatOccupant,
        target: existingBatch,
      });
    }

    return {
      status: 'open',
      aliasIds,
      existingBatch: existingBatch ?? chatOccupant,
    };
  }

  const isolatedAliasIds = aliasIds.filter((aliasId) => aliasId !== chatId);
  const occupantIsForeign =
    chatOccupant !== existingBatch ||
    !batchTouchesToolCallIds({ batch: chatOccupant, toolCallIds });
  const occupantFlushInFlight = hasInFlightFlush({
    batches,
    inFlightFlushes,
    occupant: chatOccupant,
  });

  if (
    occupantIsForeign &&
    isolatedAliasIds.length > 0 &&
    (occupantFlushInFlight || chatOccupant.adoptedOntoChatId)
  ) {
    return {
      status: 'open',
      aliasIds,
      existingBatch:
        existingBatch && existingBatch !== chatOccupant
          ? existingBatch
          : undefined,
      deferLostExecute: occupantFlushInFlight,
    };
  }

  if (
    existingBatch &&
    existingBatch !== chatOccupant &&
    resumeGroupId &&
    batches.get(resumeGroupId) === chatOccupant &&
    batchTouchesToolCallIds({ batch: existingBatch, toolCallIds }) &&
    !occupantFlushInFlight &&
    !chatOccupant.adoptedOntoChatId
  ) {
    absorbExternalToolBatch({
      batches,
      source: chatOccupant,
      target: existingBatch,
    });

    return { status: 'open', aliasIds, existingBatch };
  }

  if (occupantIsForeign && chatOccupant !== existingBatch) {
    tombstoneIncomingWave({
      batchDeclarationGates,
      batchId,
      resumeGroupId,
    });

    return { status: 'ignored' };
  }

  return { status: 'open', aliasIds, existingBatch };
}
